// components/Store/ProductCard.tsx
import * as React from 'react';
import Image from 'next/image';
import { formatISK } from '@/utils/utils';
import ImagePlaceholder from '../common/ImagePlaceholder';

/* ----------------------------- types & helpers ----------------------------- */
type ImageAsset = { url: string; title?: string };

type ProductCardProps = {
  productId: string;
  name: string;
  category: string;
  group: string;
  price: number;
  oldPrice?: number;
  available: boolean;
};

/* --------------------------------- Card ------------------------------------ */
const ProductCard: React.FC<ProductCardProps> = ({ productId, name, group, price, oldPrice, available }) => {
  const [images, setImages] = React.useState<ImageAsset[] | null>(null);
  const [idx, setIdx] = React.useState(0);

  // Fetch images for this productId via API route (token stays server-side)
  React.useEffect(() => {
    let cancelled = false;
    setImages(null);
    setIdx(0);

    (async () => {
      try {
        const r = await fetch(`/api/contentful/card-images?id=${encodeURIComponent(productId)}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (!cancelled) {
          const list: ImageAsset[] = Array.isArray(json?.images) ? json.images : [];
          const unique = Array.from(new Map(list.map((i) => [i.url, i])).values());
          setImages(unique);
        }
      } catch {
        if (!cancelled) setImages([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const hasImages = Array.isArray(images) && images.length > 0;
  const canStep = hasImages && images.length > 1;

  // Keep idx in range if images change
  React.useEffect(() => {
    if (!hasImages) return;
    if (idx > images!.length - 1) setIdx(0);
  }, [hasImages, images, idx]);

  const active = hasImages ? images[Math.min(idx, images.length - 1)] : null;

  return (
    <div className="flex h-[500px] w-full flex-col gap-24 overflow-hidden rounded-2xl bg-white-50 shadow-md transition-all duration-200 hover:shadow-lg">
      {/* Image area */}
      <div className="relative h-[260px] w-full select-none bg-white-100 p-24">
        {images === null && <div className="bg-gray-100 h-full w-full animate-pulse" />}

        {active && (
          <div className="relative h-full w-full">
            {/* key ensures full swap so you *see* the change */}
            <Image
              key={active.url || `img-${idx}`}
              alt={active.title || name || 'vara'}
              src={`${active.url}?w=1000&q=80`}
              fill
              className="p-6 object-contain"
              sizes="(max-width: 640px) 100vw, 25vw"
              priority={false}
              draggable={false}
            />

            {canStep && (
              <div
                className="absolute -bottom-16 left-0 right-0 z-10 flex justify-center gap-8"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {images!.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Fara á mynd ${i + 1}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIdx(i);
                    }}
                    className={`h-12 w-12 rounded-full border border-white-5 ${
                      i === idx ? 'bg-black-75' : 'bg-black-25'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {images && images.length === 0 && <ImagePlaceholder />}
        <div className="relative h-0">
          {!available && (
            <div className="absolute -left-24 bottom-[-36px] w-fit rounded-r-8 bg-primary-light-green px-24 py-8 text-[14px] font-bold tracking-[0.1px] text-primary-deep-sea">
              <span>Uppselt</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mb-24 flex flex-1 flex-col justify-between px-24">
        <div>
          <span className="text-xs mt-1 text-[12px] text-neutral-10">{group}</span>
          <h2 className="text-gray-900 line-clamp-2 text-[24px] font-semibold">{name}</h2>
        </div>

        <div className="mt-3 flex flex-col items-start justify-start text-primary-red">
          <span className="text-[12px]">Verð frá:</span>
          <span className="text-lg font-bold">{formatISK(price)}</span>
          <span className={`text-[14px] font-bold line-through ${!!oldPrice ? '' : 'invisible'}`}>
            {formatISK(oldPrice)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
